import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  TextField,
  Tabs,
  Tab,
  TextareaAutosize,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import PendingIcon from "@mui/icons-material/Pending";
import HistoryIcon from "@mui/icons-material/History";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FinanceSalaryApproval = () => {
  const userRole = (localStorage.getItem('role') || localStorage.getItem('userRole') || 'finance').toLowerCase();
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Data states
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvedPayrolls, setApprovedPayrolls] = useState([]);
  const [paidPayrolls, setPaidPayrolls] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedExport, setSelectedExport] = useState(null);

  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [transactionId, setTransactionId] = useState('');

  // Employee list for modal
  const [employeeListDialogOpen, setEmployeeListDialogOpen] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeListLoading, setEmployeeListLoading] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeListExportInfo, setEmployeeListExportInfo] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setFetchError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      const [pendingRes, approvedRes, paidRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/salary-export/pending-approvals`, { headers }),
        axios.get(`${API_URL}/api/salary-export/approved-payrolls`, { headers }),
        axios.get(`${API_URL}/api/salary-export/paid-payrolls`, { headers }),
        axios.get(`${API_URL}/api/salary-export/stats`, { headers })
      ]);

      if (pendingRes.data.success) setPendingApprovals(pendingRes.data.data || []);
      if (approvedRes.data.success) setApprovedPayrolls(approvedRes.data.data || []);
      if (paidRes.data.success) setPaidPayrolls(paidRes.data.data || []);
      if (statsRes.data.success) setStats(statsRes.data.data);

    } catch (error) {
      //console.error("Error fetching data:", error);
      setFetchError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    handleMenuClose();
  };

  // Menu handlers
  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  const handleMarkAsUploaded = async (exportId) => {
    if (!window.confirm("Mark this payroll as uploaded to the bank portal and notify the Finance Manager?")) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/mark-uploaded/${exportId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage("🏦 Payroll marked as uploaded successfully. Finance Manager notified!");
        fetchAllData();
        if (detailsDialogOpen) setDetailsDialogOpen(false);
        handleMenuClose();
      }
    } catch (error) {
      //console.error("Error marking payroll as uploaded:", error);
      alert(error.response?.data?.message || "Failed to mark payroll as uploaded.");
    }
  };

  const handleApprove = async (exportId) => {
    if (!window.confirm("Are you sure you want to approve this payroll?")) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/approve/${exportId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage("✅ Payroll approved successfully!");
        fetchAllData();
        if (detailsDialogOpen) setDetailsDialogOpen(false);
        handleMenuClose();
      }
    } catch (error) {
      //console.error("Error approving payroll:", error);
      alert("Failed to approve payroll. Please try again.");
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/reject/${selectedExport.id}`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage("❌ Payroll rejected successfully!");
        setRejectDialogOpen(false);
        setRejectReason('');
        fetchAllData();
        if (detailsDialogOpen) setDetailsDialogOpen(false);
        handleMenuClose();
      }
    } catch (error) {
      //console.error("Error rejecting payroll:", error);
      alert("Failed to reject payroll. Please try again.");
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/mark-paid/${selectedExport.id}`,
        { transaction_id: transactionId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage(" Payroll marked as paid successfully!");
        setPaymentDialogOpen(false);
        setTransactionId('');
        fetchAllData();
        if (detailsDialogOpen) setDetailsDialogOpen(false);
        handleMenuClose();
      }
    } catch (error) {
      //console.error("Error marking as paid:", error);
      alert("Failed to mark as paid. Please try again.");
    }
  };

  const handleMarkAsFailed = async (exportId) => {
    const reason = prompt("Please provide a reason for failure:");
    if (reason === null) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/mark-failed/${exportId}`,
        { error_details: reason || 'Bank processing failed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage("Payroll marked as failed.");
        fetchAllData();
        if (detailsDialogOpen) setDetailsDialogOpen(false);
        handleMenuClose();
      }
    } catch (error) {
      //console.error("Error marking as failed:", error);
      alert("Failed to mark as failed. Please try again.");
    }
  };

  const handleViewDetails = (exportRecord) => {
    setSelectedExport(exportRecord);
    setDetailsDialogOpen(true);
    handleMenuClose();
  };

  const handleViewEmployeeList = async (exportRecord) => {
    if (!exportRecord || !exportRecord.id) return;
    setEmployeeListExportInfo(exportRecord);
    setEmployeeListDialogOpen(true);
    setEmployeeListLoading(true);
    setEmployeeSearchTerm('');
    handleMenuClose();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/salary-export/export-employees/${exportRecord.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setEmployeeList(response.data.data || []);
      } else {
        setEmployeeList([]);
      }
    } catch (error) {
      //console.error("Error fetching employee list:", error);
      alert("Failed to fetch employee list for this export.");
      setEmployeeList([]);
    } finally {
      setEmployeeListLoading(false);
    }
  };

  const handleDownloadEmployeesExcel = async () => {
    const exportId = employeeListExportInfo?.id || selectedExport?.id;
    if (!exportId) {
      alert("No export record selected for download");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/salary-export/download/${exportId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = employeeListExportInfo?.file_name || `Salary_${employeeListExportInfo?.month_year || 'Export'}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      //console.error("Error downloading bank format excel file:", error);
      alert("Failed to download bank format salary file. Downloading standard table data...");

      if (!employeeList || employeeList.length === 0) return;
      const exportData = employeeList.map((emp, index) => ({
        'S.No': index + 1,
        'Employee ID': emp.employee_id,
        'Employee Name': emp.name,
        'Bank Name': emp.bank_name,
        'Account Holder Name': emp.account_holder,
        'Account Number': emp.account_number,
        'IFSC Code': emp.ifsc_code,
        'Net Salary (INR)': emp.net_salary
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Employees Salary");
      const monthYear = employeeListExportInfo?.month_year ? employeeListExportInfo.month_year.replace(/\s+/g, '_') : 'Salary';
      XLSX.writeFile(workbook, `Salary_Employees_${monthYear}.xlsx`);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      'Pending Approval': { color: 'warning', label: 'Pending' },
      'Uploaded to Bank': { color: 'info', label: 'Uploaded' },
      'Approved': { color: 'success', label: 'Approved' },
      'Rejected': { color: 'error', label: 'Rejected' },
      'Paid': { color: 'success', label: 'Paid' },
      'Failed': { color: 'error', label: 'Failed' }
    };
    const config = statusMap[status] || statusMap['Pending Approval'];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Render action menu items based on status and role
  const renderMenuItems = (record) => {
    const items = [];

    if (record.status === 'Pending Approval') {
      if (userRole === 'finance') {
        items.push(
          <MenuItem key="upload" onClick={() => handleMarkAsUploaded(record.id)}>
            <ListItemIcon><AccountBalanceIcon color="info" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Mark Uploaded to Bank" />
          </MenuItem>
        );
      } else {
        items.push(
          <MenuItem key="approve" onClick={() => handleApprove(record.id)}>
            <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Approve" sx={{ color: 'success.main' }} />
          </MenuItem>
        );
        items.push(
          <MenuItem key="reject" onClick={() => {
            setSelectedExport(record);
            setRejectDialogOpen(true);
            handleMenuClose();
          }}>
            <ListItemIcon><CancelIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Reject" sx={{ color: 'error.main' }} />
          </MenuItem>
        );
      }
      items.push(<Divider key="divider1" />);
    }

    if (record.status === 'Uploaded to Bank' && (userRole === 'finance_manager' || userRole === 'admin')) {
      items.push(
        <MenuItem key="approve" onClick={() => handleApprove(record.id)}>
          <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Approve" sx={{ color: 'success.main' }} />
        </MenuItem>
      );
      items.push(
        <MenuItem key="reject" onClick={() => {
          setSelectedExport(record);
          setRejectDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon><CancelIcon color="error" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Reject" sx={{ color: 'error.main' }} />
        </MenuItem>
      );
      items.push(<Divider key="divider1" />);
    }

    if (record.status === 'Approved') {
      items.push(
        <MenuItem key="markPaid" onClick={() => {
          setSelectedExport(record);
          setPaymentDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon><CurrencyRupeeIcon color="primary" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Mark as Paid" />
        </MenuItem>
      );
      // items.push(
      //   <MenuItem key="markFailed" onClick={() => handleMarkAsFailed(record.id)}>
      //     <ListItemIcon><CancelIcon color="error" fontSize="small" /></ListItemIcon>
      //     <ListItemText primary="Mark as Failed" sx={{ color: 'error.main' }} />
      //   </MenuItem>
      // );
      items.push(<Divider key="divider2" />);
    }

    // Common items for all
    items.push(
      <MenuItem key="viewDetails" onClick={() => handleViewDetails(record)}>
        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="View Details" />
      </MenuItem>
    );
    items.push(
      <MenuItem key="viewEmployees" onClick={() => handleViewEmployeeList(record)}>
        <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="View Employees" />
      </MenuItem>
    );

    return items;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 3 }}>
        <Alert severity="error">{fetchError}</Alert>
        <Button variant="contained" onClick={fetchAllData} sx={{ mt: 2 }} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, margin: 'auto', padding: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Salary Authorization
      </Typography>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={() => setSuccessMessage(null)}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      )}

  {/* Stats Cards */}
<Grid container spacing={3} sx={{ mb: 4 }}>
  <Grid item xs={12} sm={6} md={3}>
    <Paper
      sx={{
        p: 3,
        bgcolor: "#fff",
        borderLeft: "6px solid #ff9800",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Typography
        variant="h6"
        color="warning.main"
        sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}
      >
        <PendingIcon />
        {stats?.pending_approval || 0}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Pending Approval
      </Typography>
    </Paper>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Paper
      sx={{
        p: 3,
        bgcolor: "#fff",
        borderLeft: "6px solid #4caf50",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Typography
        variant="h6"
        color="success.main"
        sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}
      >
        <CheckCircleIcon />
        {stats?.approved || 0}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Approved
      </Typography>
    </Paper>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Paper
      sx={{
        p: 3,
        bgcolor: "#fff",
        borderLeft: "6px solid #1976d2",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Typography
        variant="h6"
        color="primary.main"
        sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}
      >
        <CurrencyRupeeIcon />
        {stats?.paid || 0}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Paid
      </Typography>
    </Paper>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Paper
      sx={{
        p: 3,
        bgcolor: "#fff",
        borderLeft: "6px solid #f44336",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Typography
        variant="h6"
        color="error.main"
        sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}
      >
        <CancelIcon />
        {(stats?.rejected || 0) + (stats?.failed || 0)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Rejected/Failed
      </Typography>
    </Paper>
  </Grid>
</Grid>

      {/* Main Content */}
      <Paper elevation={3} sx={{ padding: 4 }}>
              <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3 }}
        >
          <Tab label={`Pending (${pendingApprovals.length})`} />
          <Tab label={`Approved (${approvedPayrolls.length})`} />
          <Tab label={`Paid (${paidPayrolls.length})`} />
        </Tabs>


        {/* Tab 1: Pending Approvals */}
        {tabValue === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="warning.main">
                <PendingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Pending Approvals
              </Typography>
              <Button variant="outlined" size="small" onClick={fetchAllData} startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {pendingApprovals.length === 0 ? (
              <Alert severity="success">✅ No pending approvals. All exports have been reviewed.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                      <TableCell><strong>Month/Year</strong></TableCell>
                      <TableCell><strong>Employees</strong></TableCell>
                      <TableCell><strong>Total Amount</strong></TableCell>
                      <TableCell><strong>Exported By</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingApprovals.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{record.month_year}</TableCell>
                        <TableCell>{record.total_employees || 0}</TableCell>
                        <TableCell>{formatCurrency(record.total_amount)}</TableCell>
                        <TableCell>{record.exported_by_name || record.exported_by}</TableCell>
                        <TableCell>{getStatusChip(record.status)}</TableCell>
                        <TableCell align="center">
                          <IconButton onClick={(event) => handleMenuOpen(event, record)} size="small">
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 2: Approved Payrolls */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
              <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Approved Payrolls (Ready for Payment)
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {approvedPayrolls.length === 0 ? (
              <Alert severity="info">No approved payrolls waiting for payment.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                      <TableCell><strong>Month/Year</strong></TableCell>
                      <TableCell><strong>Employees</strong></TableCell>
                      <TableCell><strong>Total Amount</strong></TableCell>
                      <TableCell><strong>Approved By</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvedPayrolls.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{record.month_year}</TableCell>
                        <TableCell>{record.total_employees || 0}</TableCell>
                        <TableCell>{formatCurrency(record.total_amount)}</TableCell>
                        <TableCell>{record.approved_by || '-'}</TableCell>
                        <TableCell>{getStatusChip(record.status)}</TableCell>
                        <TableCell align="center">
                          <IconButton onClick={(event) => handleMenuOpen(event, record)} size="small">
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 3: Paid Payrolls */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="h6" color="primary.main" sx={{ mb: 2 }}>
              <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Payment History
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {paidPayrolls.length === 0 ? (
              <Alert severity="info">No payment history found.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      <TableCell><strong>Month/Year</strong></TableCell>
                      <TableCell><strong>Employees</strong></TableCell>
                      <TableCell><strong>Total Amount</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Confirmed By</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paidPayrolls.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{record.month_year}</TableCell>
                        <TableCell>{record.total_employees || 0}</TableCell>
                        <TableCell>{formatCurrency(record.total_amount)}</TableCell>
                        <TableCell>{getStatusChip(record.status)}</TableCell>
                        <TableCell>{record.confirmed_by_name || record.payment_confirmed_by}</TableCell>
                        <TableCell align="center">
                          <IconButton onClick={(event) => handleMenuOpen(event, record)} size="small">
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            minWidth: 220,
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[3],
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
      >
        {selectedRecord && renderMenuItems(selectedRecord)}
      </Menu>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Payroll Details</Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedExport && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Month/Year</Typography>
                  <Typography variant="body1"><strong>{selectedExport.month_year}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Box sx={{ mt: 1 }}>{getStatusChip(selectedExport.status)}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">File Name</Typography>
                  <Typography variant="body2">{selectedExport.file_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Bank</Typography>
                  <Typography variant="body2">{selectedExport.bankTemplate?.bank_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Employees</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body1"><strong>{selectedExport.total_employees || 0}</strong></Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewEmployeeList(selectedExport)}
                      sx={{ textTransform: 'none', py: 0.25, px: 1 }}
                    >
                      View
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Amount</Typography>
                  <Typography variant="body1" color="primary">
                    <strong>{formatCurrency(selectedExport.total_amount)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Exported At</Typography>
                  <Typography variant="body2">{new Date(selectedExport.exported_at).toLocaleString()}</Typography>
                </Grid>
                {selectedExport.approved_at && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Approved At</Typography>
                    <Typography variant="body2">{new Date(selectedExport.approved_at).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedExport.payment_confirmed_at && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Payment Confirmed At</Typography>
                    <Typography variant="body2">{new Date(selectedExport.payment_confirmed_at).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedExport.transaction_id && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Transaction ID</Typography>
                    <Typography variant="body2"><strong>{selectedExport.transaction_id}</strong></Typography>
                  </Grid>
                )}
                {selectedExport.error_details && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">Error Details</Typography>
                    <Typography variant="body2" color="error">{selectedExport.error_details}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1 }}>
          {selectedExport && selectedExport.status === 'Pending Approval' && (
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              {userRole === 'finance' ? (
                <Button
                  variant="contained"
                  color="info"
                  fullWidth
                  onClick={() => {
                    handleMarkAsUploaded(selectedExport.id);
                    setDetailsDialogOpen(false);
                  }}
                >
                  Mark Uploaded to Bank
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={() => handleApprove(selectedExport.id)}
                  >
                   Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={() => {
                      setRejectDialogOpen(true);
                      setDetailsDialogOpen(false);
                    }}
                  >
                    Reject
                  </Button>
                </>
              )}
            </Box>
          )}
          {selectedExport && selectedExport.status === 'Uploaded to Bank' && (userRole === 'finance_manager' || userRole === 'admin') && (
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={() => handleApprove(selectedExport.id)}
              >
               Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => {
                  setRejectDialogOpen(true);
                  setDetailsDialogOpen(false);
                }}
              >
                 Reject
              </Button>
            </Box>
          )}
          {selectedExport && selectedExport.status === 'Approved' && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => {
                setPaymentDialogOpen(true);
                setDetailsDialogOpen(false);
              }}
            >
               Mark as Paid
            </Button>
          )}
          <Button onClick={() => setDetailsDialogOpen(false)} variant="outlined" sx={{ mt: 1 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" color="error">Reject Payroll</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Please provide a reason for rejecting this payroll:
          </Typography>
          <TextareaAutosize
            minRows={4}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginTop: '8px',
              fontFamily: 'inherit'
            }}
            placeholder="Enter rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject Payroll
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" color="primary">Confirm Payment</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Confirm that salaries have been disbursed to all employees.
          </Typography>
          {selectedExport && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Month:</strong> {selectedExport.month_year}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Total Amount:</strong> {formatCurrency(selectedExport.total_amount)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Employees:</strong> {selectedExport.total_employees}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Transaction ID (Optional)"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Enter bank transaction reference"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleMarkAsPaid}>
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee List Dialog */}
      <Dialog
        open={employeeListDialogOpen}
        onClose={() => setEmployeeListDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
             Employee Disbursement Details {employeeListExportInfo?.month_year ? `(${employeeListExportInfo.month_year})` : ''}
            </Typography>
            <IconButton onClick={() => setEmployeeListDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search by ID, name, bank, account..."
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadEmployeesExcel}
              disabled={employeeListLoading || employeeList.length === 0}
            >
              Export Excel
            </Button>
          </Box>

          {employeeListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : employeeList.length === 0 ? (
            <Alert severity="info">No employee records found for this disbursement.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Employee ID</strong></TableCell>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell><strong>Bank Name</strong></TableCell>
                    <TableCell><strong>Account Holder</strong></TableCell>
                    <TableCell><strong>Account Number</strong></TableCell>
                    <TableCell><strong>IFSC Code</strong></TableCell>
                    <TableCell align="right"><strong>Net Salary</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeList
                    .filter((emp) => {
                      const term = employeeSearchTerm.toLowerCase();
                      return (
                        (emp.employee_id && emp.employee_id.toLowerCase().includes(term)) ||
                        (emp.name && emp.name.toLowerCase().includes(term)) ||
                        (emp.bank_name && emp.bank_name.toLowerCase().includes(term)) ||
                        (emp.account_number && emp.account_number.toLowerCase().includes(term)) ||
                        (emp.ifsc_code && emp.ifsc_code.toLowerCase().includes(term))
                      );
                    })
                    .map((emp, index) => (
                      <TableRow key={emp.employee_id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{emp.employee_id}</TableCell>
                        <TableCell><strong>{emp.name}</strong></TableCell>
                        <TableCell>{emp.bank_name}</TableCell>
                        <TableCell>{emp.account_holder}</TableCell>
                        <TableCell>{emp.account_number}</TableCell>
                        <TableCell>{emp.ifsc_code}</TableCell>
                        <TableCell align="right"><strong>{formatCurrency(emp.net_salary)}</strong></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="textSecondary">
            Total Employees: <strong>{employeeList.length}</strong> | Total Amount: <strong>{formatCurrency(employeeList.reduce((acc, curr) => acc + (curr.net_salary || 0), 0))}</strong>
          </Typography>
          <Button onClick={() => setEmployeeListDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinanceSalaryApproval;